import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '../client';
import { qk } from './keys';
import type { NotificationListResponse, NotificationSettings } from '../types';
import { withQuery } from '@/utils/url';

export type NotificationsParams = {
  cursor?: string;
  limit?: number;
  unread_only?: boolean;
  tab?: 'payment' | 'system';
  [k: string]: string | number | boolean | undefined;
};

export function useNotifications(params: NotificationsParams = {}) {
  return useQuery({
    queryKey: qk.notifications(params),
    queryFn: () => apiGet<NotificationListResponse>(withQuery('/notifications', params)),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<void>(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<void>('/notifications/mark-all-read'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: qk.notificationSettings,
    queryFn: () => apiGet<NotificationSettings>('/notifications/settings'),
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettings) => apiPut<void>('/notifications/settings', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.notificationSettings });
    },
  });
}
